import * as React from 'react';
import { Search, RotateCcw, SlidersHorizontal } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

/**
 * Generic shape of one filter chip rendered in the toolbar.
 * `select` renders a native <select>; `date_range` renders two date inputs;
 * `toggle` renders a tri-state (all/on/off) select. Keeps the component small
 * — heavier filter types (typeahead) can be added later without breaking
 * existing callers.
 */
export type FilterChip =
  | {
      id: string;
      label: string;
      type: 'select';
      value: string;
      options: Array<{ value: string; label: string }>;
      onChange: (value: string) => void;
    }
  | {
      id: string;
      label: string;
      type: 'date_range';
      value: { from: string; to: string };
      onChange: (value: { from: string; to: string }) => void;
    }
  | {
      id: string;
      label: string;
      type: 'toggle';
      value: 'all' | 'on' | 'off';
      onChange: (value: 'all' | 'on' | 'off') => void;
      onLabel?: string;
      offLabel?: string;
    }
  | {
      id: string;
      label: string;
      type: 'number_range';
      value: { min: string; max: string };
      onChange: (value: { min: string; max: string }) => void;
      placeholderMin?: string;
      placeholderMax?: string;
    };

export interface AdvancedListToolbarProps {
  /** Free-text search value (debounced upstream). */
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  /** Filter chips rendered to the right of the search box. */
  filters: FilterChip[];
  /** Sort selector. */
  sort: {
    value: string;
    onChange: (value: string) => void;
    options: Array<{ value: string; label: string }>;
  };
  /** Page-size selector. */
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  pageSizeOptions?: number[];
  /** Reset all filters back to default (called when user clicks Reset). */
  onReset: () => void;
  /** True when the current filter set differs from default (toggles Reset visibility). */
  hasActiveFilters: boolean;
  /** Optional slot for "Add" or other primary action buttons. */
  actions?: React.ReactNode;
  /** Optional result-count label (e.g. "12 residents"). */
  resultLabel?: React.ReactNode;
  className?: string;
}

const DEFAULT_PAGE_SIZES = [25, 50, 100];

/**
 * Shared toolbar for advanced listing pages — search + sort + filters + page
 * size + reset. URL-sync lives on the parent page so this stays presentational.
 *
 * Layout: search input on the left grows to fill, then filter chips wrap, then
 * sort + page-size on the right. On narrow screens everything wraps gracefully.
 */
export function AdvancedListToolbar({
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search…',
  filters,
  sort,
  pageSize,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZES,
  onReset,
  hasActiveFilters,
  actions,
  resultLabel,
  className
}: AdvancedListToolbarProps): JSX.Element {
  return (
    <div
      className={cn(
        'flex flex-col gap-vt-3 rounded-vt-md border border-vt-border bg-vt-surface p-vt-3',
        className
      )}
    >
      {/* Row 1: search + sort + page size + actions */}
      <div className="flex flex-wrap items-center gap-vt-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-vt-text-soft" />
          <Input
            className="pl-9"
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            aria-label="Search"
          />
        </div>

        <div className="flex items-center gap-vt-2">
          <SlidersHorizontal className="h-4 w-4 text-vt-text-soft" aria-hidden />
          <span className="text-vt-12 text-vt-text-muted">Sort</span>
          <Select
            className="w-48"
            value={sort.value}
            onChange={(e) => sort.onChange(e.target.value)}
            aria-label="Sort"
          >
            {sort.options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </div>

        <div className="flex items-center gap-vt-2">
          <span className="text-vt-12 text-vt-text-muted">Per page</span>
          <Select
            className="w-20"
            value={String(pageSize)}
            onChange={(e) => onPageSizeChange(parseInt(e.target.value, 10) || 50)}
            aria-label="Rows per page"
          >
            {pageSizeOptions.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </Select>
        </div>

        {actions ? <div className="ml-auto flex items-center gap-vt-2">{actions}</div> : null}
      </div>

      {/* Row 2: filter chips + reset + result label */}
      {filters.length > 0 || hasActiveFilters || resultLabel ? (
        <div className="flex flex-wrap items-end gap-vt-3 border-t border-vt-border pt-vt-3">
          {filters.map((f) => (
            <div key={f.id} className="flex flex-col gap-vt-1">
              <Label htmlFor={`flt-${f.id}`} className="text-vt-12 text-vt-text-muted">
                {f.label}
              </Label>
              {f.type === 'select' ? (
                <Select
                  id={`flt-${f.id}`}
                  className="w-44"
                  value={f.value}
                  onChange={(e) => f.onChange(e.target.value)}
                >
                  {f.options.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              ) : f.type === 'toggle' ? (
                <Select
                  id={`flt-${f.id}`}
                  className="w-40"
                  value={f.value}
                  onChange={(e) => f.onChange(e.target.value as 'all' | 'on' | 'off')}
                >
                  <option value="all">All</option>
                  <option value="on">{f.onLabel ?? 'Yes'}</option>
                  <option value="off">{f.offLabel ?? 'No'}</option>
                </Select>
              ) : f.type === 'date_range' ? (
                <div className="flex items-center gap-vt-2">
                  <Input
                    id={`flt-${f.id}`}
                    type="date"
                    className="w-36"
                    value={f.value.from}
                    onChange={(e) => f.onChange({ ...f.value, from: e.target.value })}
                    aria-label={`${f.label} from`}
                  />
                  <span className="text-vt-12 text-vt-text-soft">→</span>
                  <Input
                    type="date"
                    className="w-36"
                    value={f.value.to}
                    onChange={(e) => f.onChange({ ...f.value, to: e.target.value })}
                    aria-label={`${f.label} to`}
                  />
                </div>
              ) : (
                <div className="flex items-center gap-vt-2">
                  <Input
                    id={`flt-${f.id}`}
                    type="number"
                    className="w-28"
                    placeholder={f.placeholderMin ?? 'Min'}
                    value={f.value.min}
                    onChange={(e) => f.onChange({ ...f.value, min: e.target.value })}
                    aria-label={`${f.label} min`}
                  />
                  <span className="text-vt-12 text-vt-text-soft">→</span>
                  <Input
                    type="number"
                    className="w-28"
                    placeholder={f.placeholderMax ?? 'Max'}
                    value={f.value.max}
                    onChange={(e) => f.onChange({ ...f.value, max: e.target.value })}
                    aria-label={`${f.label} max`}
                  />
                </div>
              )}
            </div>
          ))}

          {hasActiveFilters ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onReset}
              className="self-end"
              title="Reset all filters"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Reset
            </Button>
          ) : null}

          {resultLabel ? (
            <div className="ml-auto self-end pb-vt-1 text-vt-12 text-vt-text-muted">{resultLabel}</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Tiny hook that returns + writes the URL search params via
 * `window.history.replaceState`. Avoids touching the router config (which is
 * shared with another agent's lane). Defaults are applied + dropped from the
 * URL so the address bar stays clean. Numbers / booleans are coerced.
 */
export function useUrlSearchState<T extends Record<string, string | number | undefined>>(
  defaults: T
): [T, (next: Partial<T>) => void] {
  const [state, setState] = React.useState<T>(() => readUrlState(defaults));

  // Pop / back / forward keeps page state honest with the URL.
  React.useEffect(() => {
    const onPop = (): void => setState(readUrlState(defaults));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = React.useCallback(
    (next: Partial<T>) => {
      setState((prev) => {
        const merged = { ...prev, ...next } as T;
        writeUrlState(merged, defaults);
        return merged;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return [state, update];
}

function readUrlState<T extends Record<string, string | number | undefined>>(defaults: T): T {
  if (typeof window === 'undefined') return { ...defaults };
  const params = new URLSearchParams(window.location.search);
  const out = { ...defaults } as Record<string, string | number | undefined>;
  for (const key of Object.keys(defaults)) {
    const raw = params.get(key);
    if (raw === null) continue;
    const def = defaults[key];
    if (typeof def === 'number') {
      const n = parseInt(raw, 10);
      if (!Number.isNaN(n)) out[key] = n;
    } else {
      out[key] = raw;
    }
  }
  return out as T;
}

function writeUrlState<T extends Record<string, string | number | undefined>>(state: T, defaults: T): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  for (const key of Object.keys(state)) {
    const v = state[key];
    const d = defaults[key];
    // Drop empty / default values from the URL so the address bar stays clean.
    if (v === undefined || v === null || v === '' || v === d) {
      url.searchParams.delete(key);
    } else {
      url.searchParams.set(key, String(v));
    }
  }
  window.history.replaceState({}, '', url.toString());
}
